import { useMemo, useState } from "react";
import { useEntityTypes } from "../../lib/hooks/use-kuvio";
import {
  useWrapApiFromOpenapi,
  useAddWrappedApi,
  useUpdateWrappedApi,
  isOAuthAuthKind,
  type WrapApiProposal,
  type WrappedApiAuthKind,
  type EntityTypeSummaryInput,
} from "./use-wrapped-apis";

/**
 * Sprint 16d — wizard state machine + LLM-call orchestration.
 *
 * Owns the cross-step state (spec source, the AI proposal, the editable
 * identify/operations choices, and whether the wrapper has been persisted yet)
 * and the two async transitions that touch the server: `analyze` (Step 1 →
 * `wrap_api_from_openapi`) and `persistIdentify` (Step 2 → add/update the
 * wrapper). Mapping persistence (Step 4) and live test calls (Step 5) operate on
 * the now-created wrapper via the existing mapping/tool hooks, so they live in
 * those step components.
 *
 * Note: `add_wrapped_api` fetches the spec from a URL server-side, so creating a
 * wrapper requires a spec URL. Pasted JSON drives the AI analysis (a convenience)
 * but cannot persist on its own in v1 — `persistIdentify` surfaces that clearly.
 */

export type WizardStep = 1 | 2 | 3 | 4 | 5;

export interface WizardSeed {
  specUrl?: string;
  name?: string;
  slug?: string;
}

export interface OAuthFields {
  clientId: string;
  clientSecret: string;
  authorizeUrl: string;
  tokenUrl: string;
  scopes: string;
}

export function useWrappedApiWizard(seed?: WizardSeed) {
  const entityTypes = useEntityTypes();
  const analyzeMut = useWrapApiFromOpenapi();
  const addWrapper = useAddWrappedApi();
  const updateWrapper = useUpdateWrappedApi();

  const [step, setStep] = useState<WizardStep>(1);
  const [error, setError] = useState<string | null>(null);

  // Step 1 — spec source.
  const [specUrl, setSpecUrl] = useState(seed?.specUrl ?? "");
  const [specJson, setSpecJson] = useState("");
  const [proposal, setProposal] = useState<WrapApiProposal | null>(null);

  // Step 2 — identify (pre-filled from the proposal, fully editable).
  const [slug, setSlug] = useState(seed?.slug ?? "");
  const [name, setName] = useState(seed?.name ?? "");
  const [authKind, setAuthKind] = useState<WrappedApiAuthKind>("bearer");
  const [baseUrl, setBaseUrl] = useState("");
  const [oauth, setOauth] = useState<OAuthFields>({
    clientId: "",
    clientSecret: "",
    authorizeUrl: "",
    tokenUrl: "",
    scopes: "",
  });

  // Step 3 — write-operation keys the owner has enabled (GET is always live).
  const [enabledOps, setEnabledOps] = useState<Set<string>>(new Set());

  // Persistence — the wrapper is created at the end of Step 2 so Steps 3–5 act live.
  const [created, setCreated] = useState(false);

  const isOAuth = isOAuthAuthKind(authKind);

  const entityTypeSummaries = useMemo<EntityTypeSummaryInput[]>(
    () =>
      (entityTypes.data ?? [])
        .filter((t) => !t.hide_from_synthesis)
        .map((t) => ({
          slug: t.slug,
          name: t.name,
          fields: t.fields.map((f) => ({ name: f.name, type: f.type })),
        })),
    [entityTypes.data],
  );

  /** Step 1 → run the wizard brain and pre-fill the proposal into later steps. */
  async function analyze(): Promise<boolean> {
    setError(null);
    const url = specUrl.trim();
    const json = specJson.trim();
    if (!url && !json) {
      setError("Enter an OpenAPI spec URL, or paste the spec JSON/YAML.");
      return false;
    }
    try {
      const p = await analyzeMut.mutateAsync({
        ...(json ? { openapi_spec_json: json } : { openapi_spec_url: url }),
        existing_entity_types: entityTypeSummaries,
      });
      setProposal(p);
      setSlug(p.proposed_slug);
      setName(p.proposed_name);
      setAuthKind(p.detected_auth_kind);
      setBaseUrl(p.base_url ?? "");
      // AI pre-enables write ops it recommended "enabled"; GET is always live.
      setEnabledOps(
        new Set(
          p.operations
            .filter((o) => o.method !== "GET" && o.recommended_state === "enabled")
            .map((o) => o.operation_id),
        ),
      );
      // Pre-fill OAuth config from well-known discovery, when found.
      if (p.oauth_discovery?.discovered) {
        setOauth((o) => ({
          ...o,
          authorizeUrl: p.oauth_discovery?.authorize_url ?? o.authorizeUrl,
          tokenUrl: p.oauth_discovery?.token_url ?? o.tokenUrl,
          scopes: (p.oauth_discovery?.scopes_supported ?? []).join(" ") || o.scopes,
        }));
      }
      setStep(2);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't analyse the spec.");
      return false;
    }
  }

  /** Step 2 → create (or, if revisited, update) the wrapper so later steps act live. */
  async function persistIdentify(): Promise<boolean> {
    setError(null);
    if (!name.trim()) return setError("Name is required."), false;
    if (!/^[a-z0-9][a-z0-9-]{0,31}$/.test(slug.trim())) {
      return setError("Slug must be lowercase letters, digits, and dashes (max 32)."), false;
    }
    if (isOAuth) {
      if (!oauth.clientId.trim()) return setError("OAuth client ID is required."), false;
      if (!oauth.clientSecret.trim() && !created) return setError("OAuth client secret is required."), false;
      if (!oauth.tokenUrl.trim()) return setError("OAuth token URL is required."), false;
      if (authKind === "oauth2_auth_code" && !oauth.authorizeUrl.trim()) {
        return setError("OAuth authorize URL is required for the Authorization Code grant."), false;
      }
    }
    const oauthPayload = isOAuth
      ? {
          oauth_client_id: oauth.clientId.trim(),
          ...(oauth.clientSecret.trim() ? { oauth_client_secret: oauth.clientSecret.trim() } : {}),
          oauth_token_url: oauth.tokenUrl.trim(),
          ...(authKind === "oauth2_auth_code" ? { oauth_authorize_url: oauth.authorizeUrl.trim() } : {}),
          ...(oauth.scopes.trim() ? { oauth_scopes: oauth.scopes.trim() } : {}),
        }
      : {};
    try {
      if (created) {
        await updateWrapper.mutateAsync({
          slug: slug.trim(),
          name: name.trim(),
          ...(baseUrl.trim() ? { base_url: baseUrl.trim() } : {}),
          ...oauthPayload,
        });
      } else {
        // A URL is re-fetchable; pasted JSON is frozen but works for private/
        // internal specs with no public URL. analyze() guarantees one is present.
        const specPayload = specUrl.trim()
          ? { openapi_spec_url: specUrl.trim() }
          : { openapi_spec_json: specJson.trim() };
        await addWrapper.mutateAsync({
          slug: slug.trim(),
          name: name.trim(),
          ...specPayload,
          auth_kind: authKind,
          ...(baseUrl.trim() ? { base_url: baseUrl.trim() } : {}),
          ...oauthPayload,
        });
        setCreated(true);
      }
      setStep(3);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save the wrapper.");
      return false;
    }
  }

  /** Step 3 → persist the enabled write operations onto the live wrapper. */
  async function persistOperations(): Promise<boolean> {
    setError(null);
    try {
      await updateWrapper.mutateAsync({ slug: slug.trim(), enabled_operations: [...enabledOps] });
      setStep(4);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save operations.");
      return false;
    }
  }

  function toggleOp(operationId: string, on: boolean) {
    setEnabledOps((prev) => {
      const next = new Set(prev);
      if (on) next.add(operationId);
      else next.delete(operationId);
      return next;
    });
  }

  const mappableOps = useMemo(
    () => proposal?.operations.filter((o) => o.method === "GET") ?? [],
    [proposal],
  );

  return {
    // state
    step,
    error,
    setError,
    specUrl,
    setSpecUrl,
    specJson,
    setSpecJson,
    proposal,
    slug,
    setSlug,
    name,
    setName,
    authKind,
    setAuthKind,
    baseUrl,
    setBaseUrl,
    oauth,
    setOauth,
    enabledOps,
    toggleOp,
    created,
    isOAuth,
    mappableOps,
    entityTypeSummaries,
    // pending flags
    analyzing: analyzeMut.isPending,
    persisting: addWrapper.isPending || updateWrapper.isPending,
    // transitions
    analyze,
    persistIdentify,
    persistOperations,
    goTo: setStep,
    back: () => setStep((s) => (s > 1 ? ((s - 1) as WizardStep) : s)),
  };
}

export type WizardController = ReturnType<typeof useWrappedApiWizard>;
